import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Mail, MessageSquare, User, Users } from 'lucide-react';
import { Badge, Button, Drawer, Skeleton } from '@/components/ui';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { useFormatDate } from '@/hooks/useFormatDate';
import { clubMembersService } from '@/services/clubMembers';

interface NoticeRecipient {
  memberId: string;
  name: string;
}

interface NoticeHistoryRow {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  emailSentAt: string | null;
  emailedCount: number;
  channels?: { inApp: boolean; email: boolean };
  targetMemberIds: string[] | null;
  targetSummary: {
    memberCount: number;
    recipients?: NoticeRecipient[];
  } | null;
  senderName?: string;
}

function ChannelBadges({
  channels,
  emailSentAt,
}: {
  channels?: NoticeHistoryRow['channels'];
  emailSentAt?: string | null;
}) {
  const inApp = channels?.inApp ?? true;
  const email = channels?.email ?? Boolean(emailSentAt);
  const items: Array<{ key: string; label: string; icon: typeof Mail }> = [];
  if (inApp) items.push({ key: 'inApp', label: 'In-app', icon: MessageSquare });
  if (email) items.push({ key: 'email', label: 'Email', icon: Mail });

  if (items.length === 0) {
    return <span className="text-xs text-muted-foreground">No channels</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(({ key, label, icon: Icon }) => (
        <Badge key={key} variant="outline" className="gap-1 text-xs font-normal">
          <Icon className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
          {label}
        </Badge>
      ))}
    </div>
  );
}

function formatTargetLabel(row: NoticeHistoryRow): string {
  if (!row.targetMemberIds || row.targetMemberIds.length === 0) {
    return 'All active members';
  }
  const count = row.targetSummary?.memberCount ?? row.targetMemberIds.length;
  return `${count} selected member${count !== 1 ? 's' : ''}`;
}

function formatRecipientsPreview(recipients: NoticeRecipient[]): string {
  if (recipients.length === 0) return '';
  if (recipients.length === 1) return recipients[0].name;
  if (recipients.length === 2) return `${recipients[0].name}, ${recipients[1].name}`;
  return `${recipients[0].name}, ${recipients[1].name} + ${recipients.length - 2} more`;
}

function NoticeHistorySkeleton() {
  return (
    <ul className="space-y-3" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="rounded-lg border border-border p-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="mt-2 h-3 w-1/2" />
          <Skeleton className="mt-3 h-6 w-full" />
        </li>
      ))}
    </ul>
  );
}

export interface ClubNoticeHistoryDrawerProps {
  clubId: string;
  open: boolean;
  onClose: () => void;
}

export function ClubNoticeHistoryDrawer({ clubId, open, onClose }: ClubNoticeHistoryDrawerProps) {
  const { formatDateTime } = useFormatDate();
  const [rows, setRows] = useState<NoticeHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detail, setDetail] = useState<NoticeHistoryRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const res = await clubMembersService.getClubNoticeHistory(clubId, page, 20);
      if (res.success && res.data) {
        setRows(res.data);
        setTotalPages(res.pagination?.totalPages ?? 1);
      }
    } finally {
      setLoading(false);
    }
  }, [clubId, page]);

  const openDetail = useCallback(
    async (row: NoticeHistoryRow) => {
      setDetail(row);
      setDetailLoading(true);
      try {
        const res = await clubMembersService.getClubNoticeDetail(clubId, row.id);
        if (res.success && res.data) {
          setDetail(res.data);
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [clubId]
  );

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setDetailLoading(false);
      setPage(1);
    }
  }, [open]);

  const handleClose = () => {
    setDetail(null);
    onClose();
  };

  const recipients = detail?.targetSummary?.recipients ?? [];

  return (
    <Drawer
      isOpen={open}
      onClose={handleClose}
      title={detail ? detail.title : 'Notice history'}
      description={
        detail
          ? `Sent ${formatDateTime(detail.publishedAt)}`
          : 'Review past notices sent to club members.'
      }
      width="lg"
    >
      {detail ? (
        <div className="space-y-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 gap-1.5 text-muted-foreground"
            onClick={() => setDetail(null)}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to list
          </Button>

          <dl className="grid gap-4 rounded-lg border border-border bg-muted/30 p-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Sent by
              </dt>
              <dd className="mt-1 flex items-center gap-1.5 font-medium text-foreground">
                <User className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                {detail.senderName ?? 'Staff'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Sent at
              </dt>
              <dd className="mt-1 font-medium text-foreground">
                {formatDateTime(detail.publishedAt)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Audience
              </dt>
              <dd className="mt-1 font-medium text-foreground">{formatTargetLabel(detail)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Channels
              </dt>
              <dd>
                <ChannelBadges channels={detail.channels} emailSentAt={detail.emailSentAt} />
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Delivery
              </dt>
              <dd className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                {(detail.channels?.inApp ?? true) && <span>In-app notice published</span>}
                {detail.channels?.email ? (
                  <span>
                    {detail.emailedCount} email{detail.emailedCount !== 1 ? 's' : ''} sent
                  </span>
                ) : null}
              </dd>
            </div>
          </dl>

          <section aria-labelledby="notice-body-heading">
            <h3
              id="notice-body-heading"
              className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Message
            </h3>
            <div className="rounded-lg border border-border bg-background p-4">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {detail.body}
              </p>
            </div>
          </section>

          <section aria-labelledby="notice-recipients-heading">
            <h3
              id="notice-recipients-heading"
              className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              <Users className="h-3.5 w-3.5" aria-hidden />
              Recipients
            </h3>
            {detailLoading ? (
              <div className="space-y-2" aria-busy="true">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : recipients.length > 0 ? (
              <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-background">
                {recipients.map((recipient) => (
                  <li key={recipient.memberId} className="px-3 py-2.5">
                    <p className="text-sm font-medium text-foreground">{recipient.name}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{formatTargetLabel(detail)}</p>
            )}
          </section>
        </div>
      ) : loading ? (
        <NoticeHistorySkeleton />
      ) : rows.length === 0 ? (
        <DashboardEmptyStateCard className="border-0 shadow-none">
          <DashboardEmptyStateIcon icon={MessageSquare} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No notices yet</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Notices you send to members will appear here.
          </p>
        </DashboardEmptyStateCard>
      ) : (
        <div className="space-y-4">
          <ul className="space-y-3">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => void openDetail(row)}
                  className="w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{row.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDateTime(row.publishedAt)} · {row.senderName ?? 'Staff'}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-xs font-normal">
                      {formatTargetLabel(row)}
                    </Badge>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{row.body}</p>
                  {row.targetSummary?.recipients && row.targetSummary.recipients.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {formatRecipientsPreview(row.targetSummary.recipients)}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
